import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  describeStorageError,
  getStorageProfile,
  getStorageQuotesInbox,
  isStorageNotApproved,
  type StorageProfile,
  type StorageRequestSummary,
  type StorageType
} from '@/shared/storage-api';
import { formatRelativeTime } from '@/shared/format';
import {
  fallbackStorageTypeLabel,
  periodChipLabel,
  QUOTE_DISABLED_REASON,
  spaceChipLabel,
  transportLabel,
  volumeLine
} from '@/pages/storage-request-format';
import { serviceOptionsOnly } from '@/shared/service-option-kinds';
import { getServiceOptions, getStorageTypeOptions } from '@/shared/storage-services-api';
import { useStorageModuleState } from '@/shared/useStorageModuleState';
import { STORAGE_REQUESTS_CLOSED_NOTICE } from '@/shared/storage-module';
import { PackageIcon } from '@/shared/icons';
import { CHIP_WRAP } from '@/shared/chip-wrap';
import { Alert, Button, Chip, EmptyState, FileThumb, ListPage, Spinner } from '@nexvia-logistics/ui';
import { Archive, Calendar, Warehouse } from '@nexvia-logistics/ui/icons';

/**
 * NEX-1164 — 'not-approved': the inbox answered 403 `STORAGE_NOT_APPROVED` (an incomplete, rejected or
 * suspended account; `pending_review` and `verified` read it). That is the account's state, not a
 * failure, so the page says what to do instead of showing a critical error.
 */
type LoadState = 'loading' | 'ready' | 'error' | 'not-approved';

/**
 * NEX-1165 — catalog labels for what a request carries as codes (NEX-1166): its storage type
 * (`kind` 'storage_type') and the services the client asked for (`neededOptions`, `kind` 'service').
 * Best effort, like Mis cotizaciones: without the catalog a code shows as sent (the seeded storage
 * types fall back to `STORAGE_TYPE_LABELS`), and a code the catalog no longer lists (deactivated,
 * or an old request's) shows as sent too.
 */
interface CatalogLabels {
  storageTypes: Map<string, string>;
  services: Map<string, string>;
}

const NO_CATALOG_LABELS: CatalogLabels = { storageTypes: new Map(), services: new Map() };

/**
 * NEX-1165 design pass — what `CotizarPage` leaves in `location.state` after a quote goes out; the
 * list shows the success notice from it (the dialog used to set it directly).
 */
const QUOTE_SENT_NOTICE = 'Enviaste tu cotización. Podés verla en "Mis cotizaciones".';

function noticeFromState(state: unknown): string | null {
  return (state as { notice?: unknown } | null)?.notice === 'quote-sent' ? QUOTE_SENT_NOTICE : null;
}

/**
 * Audit S1 — the page says why "Cotizar" is off (`QUOTE_DISABLED_REASON`, shared with the Cotizar
 * page) instead of letting the depósito fill the form to be refused.
 */
const QUOTE_DISABLED_REASON_ID = 'quote-disabled-reason';

/** NEX-1165 — `?requestId=` from the new-request mail's link; anything but a positive integer is ignored. */
function linkedRequestIdFrom(value: string | null): number | null {
  const id = Number(value);
  return value && Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * Per-`storageType` marker icon, left of each row's space-type heading (Claude Design mockup gap,
 * NEX-1164 follow-up — purely presentational, `storageType` was already fetched and shown via its
 * `STORAGE_TYPE_LABELS` chip, just never iconified). Not added to the shared `src/shared/icons.tsx`
 * re-export layer: that file is owned by a sibling agent this same pass, and these two names are
 * single-call-site glyphs, not a reusable nav icon.
 *   - `privado` -> `Archive`: a single lidded storage box/crate — the closest real glyph to "one
 *     sealed, exclusive unit", which is what "privado" means here.
 *   - `compartido` -> `Warehouse`: a whole warehouse building — reads as communal floor space
 *     shared across clients, vs. `Buildings`' "several separate properties" (a different idea:
 *     multiple distinct locations, not one shared one) or `Package`'s "generic parcel" (no
 *     storage-space connotation at all).
 *   - any other code (NEX-1165, A9: 'otro' or a type added to the catalog) -> `PackageIcon`, the
 *     neutral parcel, so it is never marked with the "privado" box.
 */
const STORAGE_TYPE_ICON: Partial<Record<StorageType, typeof Archive>> = {
  privado: Archive,
  compartido: Warehouse
};

/**
 * `GET /storage/quotes/inbox` (contract §"Cotizaciones del depósito"): the zone's published
 * requests this depósito has not quoted yet. The heading stays rendered across every load state on
 * purpose — router.test.tsx asserts it synchronously, before this page's own fetch resolves.
 *
 * NEX-1164 (2026-10-15): `ListPage` with one row per request inside its panel (spec D-2.1) — the
 * outlined `Surface` cards became rows separated by hairlines.
 *
 * NEX-1165 — opened from the new-request mail (`/solicitudes?requestId=<id>`, kept across `/login`
 * by `returnTo`), the page highlights that request and scrolls to it; when it is no longer in the
 * inbox (already quoted, or closed by the client) it says so instead of leaving the depósito to look
 * for it. Highlighted rather than opening the quote form: the inbox is also readable by an account
 * still under review, which cannot quote yet (NEX-1164, "leer sí, cotizar no").
 *
 * NEX-1165 design pass (spec 2026-10-18 §3.1) — "Solicitudes de guardado": each row has the mock's
 * three zones (type icon + "≈ 8 m³ · privado" + "#id · zona"; period, size and needed-service chips;
 * time and Cotizar). The client's name is left to the Cotizar page. "Cotizar" opens
 * `/solicitudes/:id/cotizar` (`CotizarPage.tsx`, decision D-2.1); after a quote goes out that page
 * comes back here with `state.notice`, and the list shows the success notice.
 *
 * NEX-1645 — module in "solo registro" (`GET /app/features` → `registration_only`): clients cannot
 * request a guardado yet, so the page says so and offers no Cotizar (the API would refuse the quote,
 * 503 `STORAGE_REQUESTS_DISABLED`). The inbox is still read and listed: the depósito keeps seeing
 * what its zone has, and the rest of the panel works as usual.
 */
export default function SolicitudesPage() {
  const [state, setState] = useState<LoadState>('loading');
  const [requests, setRequests] = useState<StorageRequestSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [notice] = useState<string | null>(() => noticeFromState(location.state));
  const [searchParams] = useSearchParams();
  const linkedRequestId = linkedRequestIdFrom(searchParams.get('requestId'));
  const linkedRowRef = useRef<HTMLLIElement | null>(null);
  const [catalogLabels, setCatalogLabels] = useState<CatalogLabels>(NO_CATALOG_LABELS);
  // Audit S1 — the account's status from `GET /storage/me`; `null` until it answers (or when it fails:
  // [ASSUMED] the button stays on then, and the API still refuses a quote from an account not verified).
  const [approvalStatus, setApprovalStatus] = useState<StorageProfile['approvalStatus'] | null>(null);
  const canQuote = !approvalStatus || approvalStatus === 'verified';
  // NEX-1645 — `null` (still loading; the shell waits for it, so rare here) and a failed read
  // (`full`, [ASSUMED] in the hook) keep Cotizar offered.
  const requestsClosed = useStorageModuleState() === 'registration_only';

  useEffect(() => {
    let cancelled = false;
    getStorageProfile()
      .then((profile) => {
        if (!cancelled) setApprovalStatus(profile.approvalStatus);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Separate from the inbox load on purpose: the labels are an embellishment, and a catalog failure
  // must never keep the depósito from reading (and quoting) its requests.
  useEffect(() => {
    let cancelled = false;
    Promise.all([getStorageTypeOptions().catch(() => []), getServiceOptions().catch(() => [])]).then(
      ([storageTypes, catalog]) => {
        if (cancelled) return;
        setCatalogLabels({
          storageTypes: new Map(storageTypes.map((option) => [option.code, option.label])),
          services: new Map(serviceOptionsOnly(catalog).map((option) => [option.code, option.label]))
        });
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setState('loading');
      setError(null);
      try {
        const data = await getStorageQuotesInbox();
        if (!cancelled) {
          setRequests(data);
          setState('ready');
        }
      } catch (err) {
        if (cancelled) return;
        if (isStorageNotApproved(err)) {
          setState('not-approved');
          return;
        }
        setError(describeStorageError(err, 'No pudimos cargar las solicitudes de tu zona.'));
        setState('error');
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const linkedRequestListed = linkedRequestId !== null && requests.some((request) => request.id === linkedRequestId);

  useEffect(() => {
    if (state === 'ready' && linkedRequestListed) {
      // Optional call: jsdom has no scrollIntoView.
      linkedRowRef.current?.scrollIntoView?.({ block: 'center' });
    }
  }, [state, linkedRequestListed]);

  // The notice is read once on arrival; the history entry drops it so a reload or a "back" to this
  // entry does not announce the same quote again. Pathname and query stay as they were.
  useEffect(() => {
    if (noticeFromState(location.state)) {
      navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  // The list's query (`?requestId=` from the mail) travels to the Cotizar page, whose way back
  // returns to it: the row stays highlighted when the depósito comes back without quoting.
  const openQuotePage = (requestId: number) =>
    navigate(`/solicitudes/${requestId}/cotizar`, location.search ? { state: { listSearch: location.search } } : undefined);

  const linkedRequestGone = state === 'ready' && linkedRequestId !== null && !linkedRequestListed;
  // With requests closed there is no Cotizar button for the approval reason to describe.
  const quoteDisabledShown = state === 'ready' && requests.length > 0 && !canQuote && !requestsClosed;

  return (
    <ListPage
      title="Solicitudes de guardado"
      description="Llegan las solicitudes de las zonas donde tenés depósitos. Cotizás las que te interesan; el usuario elige."
      notice={
        notice ? (
          <Alert tone="positive">{notice}</Alert>
        ) : requestsClosed || linkedRequestGone || quoteDisabledShown ? (
          <div className="flex flex-col gap-3">
            {requestsClosed && <Alert tone="info">{STORAGE_REQUESTS_CLOSED_NOTICE}</Alert>}
            {linkedRequestGone && (
              <Alert tone="info">
                La solicitud del mail ya no está para cotizar: puede que ya la hayas cotizado (mirala en &quot;Mis
                cotizaciones&quot;) o que el cliente la haya cerrado.
              </Alert>
            )}
            {quoteDisabledShown && (
              <Alert tone="caution">
                <span id={QUOTE_DISABLED_REASON_ID}>{QUOTE_DISABLED_REASON}</span>
              </Alert>
            )}
          </div>
        ) : undefined
      }
    >
      {state === 'loading' && (
        <p className="m-0 flex items-center gap-2 p-5 text-ui-body text-on-surface-variant">
          <Spinner size={18} label={null} />
          Cargando solicitudes…
        </p>
      )}

      {state === 'error' && (
        <div className="p-5">
          <Alert tone="critical">{error}</Alert>
        </div>
      )}

      {state === 'not-approved' && (
        <EmptyState
          icon={<PackageIcon size={24} />}
          title="Todavía no podés ver solicitudes"
          description="Vas a ver las solicitudes de tu zona cuando tu alta esté completa y en revisión o aprobada por Nexvia. Revisá el estado de tu cuenta y tu documentación en Mi empresa."
          action={
            <Link to="/empresa" className="text-ui-label text-primary hover:underline">
              Ir a Mi empresa
            </Link>
          }
        />
      )}

      {state === 'ready' && requests.length === 0 && (
        <EmptyState icon={<PackageIcon size={24} />} title="No hay solicitudes nuevas en tu zona por el momento." />
      )}

      {state === 'ready' && requests.length > 0 && (
        <ul className="m-0 list-none p-0">
          {requests.map((request) => {
            const StorageTypeIcon = STORAGE_TYPE_ICON[request.storageType] ?? PackageIcon;
            const isLinked = request.id === linkedRequestId;
            const storageTypeLabel =
              catalogLabels.storageTypes.get(request.storageType) ?? fallbackStorageTypeLabel(request.storageType);
            return (
              <li
                key={request.id}
                ref={isLinked ? linkedRowRef : undefined}
                aria-current={isLinked ? 'true' : undefined}
                className={`grid grid-cols-[minmax(0,1fr)] gap-3 p-4 shadow-[inset_0_-1px_0_var(--nx-outline-muted)] last:shadow-none md:grid-cols-[minmax(0,1.2fr)_minmax(0,2fr)_auto] md:items-center md:gap-5 md:p-5${isLinked ? ' bg-tone-info-bg' : ''}`}
              >
                <div className="flex min-w-0 items-start gap-3">
                  <span className="grid size-10 shrink-0 place-items-center rounded-card bg-surface-container text-on-surface-variant">
                    <StorageTypeIcon size={20} />
                  </span>
                  <div className="min-w-0">
                    <h2 className="m-0 text-ui-h3 text-on-surface">{volumeLine(request, storageTypeLabel)}</h2>
                    <p className="m-0 text-ui-small text-on-surface-variant">
                      {`#${request.id} · ${request.locality}, ${request.department}`}
                    </p>
                  </div>
                </div>

                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                  <Chip tone="neutral" icon={<Calendar size={14} />} className={CHIP_WRAP}>
                    {periodChipLabel(request)}
                  </Chip>
                  <Chip tone="neutral" icon={<Archive size={14} />} className={CHIP_WRAP}>
                    {spaceChipLabel(request)}
                  </Chip>
                  {request.neededOptions.map((option) => (
                    <Chip key={option} tone="neutral" className={CHIP_WRAP}>
                      {catalogLabels.services.get(option) ?? option}
                    </Chip>
                  ))}
                  <Chip tone={request.needsTransport ? 'info' : 'neutral'}>{transportLabel(request)}</Chip>
                  {isLinked && <Chip tone="positive">La solicitud del mail</Chip>}
                </div>

                <div className="flex items-center gap-3 md:justify-self-end">
                  <span className="whitespace-nowrap text-ui-small text-on-surface-variant">
                    {formatRelativeTime(request.createdAt)}
                  </span>
                  {!requestsClosed && (
                    <Button
                      type="button"
                      onClick={() => openQuotePage(request.id)}
                      size="xs"
                      showArrow={false}
                      disabled={!canQuote}
                      aria-describedby={canQuote ? undefined : QUOTE_DISABLED_REASON_ID}
                    >
                      Cotizar
                    </Button>
                  )}
                </div>

                {/* NEX-1166 — the client's photos, signed by the API on every inbox read (never
                    stored signed), so they always load after a reload. Each opens full size. The
                    mock leaves them to Cotizar; kept at the row's end because they are real, useful
                    data (spec §3.1). */}
                {request.photos.length > 0 && (
                  <ul aria-label="Fotos de la solicitud" className="m-0 flex list-none flex-wrap gap-2 p-0 md:col-span-3">
                    {request.photos.map((photo, index) => (
                      <li key={photo}>
                        <a href={photo} target="_blank" rel="noreferrer" className="block rounded-card">
                          <FileThumb url={photo} label={`Foto ${index + 1} de la solicitud`} />
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </ListPage>
  );
}
